import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  DetailCloseButton,
  DetailSidebarFallback,
  DocumentPreviewSection,
  LineItemsSection,
  StatusBadge,
} from "@/accounts-payable/common";
import { AgentActionsSection } from "@/accounts-payable/invoice-detail/agent-actions-section";
import { InvoiceActivitySection } from "@/accounts-payable/invoice-detail/invoice-activity-section";
import {
  useGetDocumentAgentRuns,
  useGetReceipt,
} from "@/accounts-payable/query";
import {
  getActiveRun,
  getLatestCompletedRun,
  invoiceStatusMeta,
  runsInFlightRefetchInterval,
} from "@/accounts-payable/utils";
import { ReceiptFieldsSection } from "./receipt-fields-section";

/** Side panel for one receipt: its fields, the document, and agent actions. */
export const ApReceiptDetail = ({
  receiptId,
  businessId,
  onClose,
  onViewAgentReview,
}: {
  receiptId: string;
  businessId: string | null;
  onClose: () => void;
  onViewAgentReview?: (receiptId: string) => void;
}) => {
  const { data: receipt, isPending } = useGetReceipt(businessId, receiptId);
  const {
    data: runs = [],
    isLoading: runsLoading,
    isError: runsError,
  } = useGetDocumentAgentRuns(
    businessId,
    "receipt",
    receiptId,
    runsInFlightRefetchInterval,
  );

  if (!receipt) {
    return (
      <DetailSidebarFallback
        loading={isPending}
        errorMessage="Couldn’t load the receipt. Please try again."
        onClose={onClose}
      />
    );
  }

  return (
    <Stack gap="none" fullWidth className="h-full">
      <Stack
        gap="sm"
        fullWidth
        className="flex-none border-b border-stroke-tertiary bg-bg-secondary/50 p-6"
      >
        <Stack
          direction="row"
          justify="between"
          align="start"
          gap="md"
          fullWidth
        >
          <Stack direction="row" gap="sm" align="center" className="flex-wrap">
            <StatusBadge meta={invoiceStatusMeta(receipt)} />
            <Typography textColor="tertiary">
              {receipt.receipt_number ?? "Receipt"}
            </Typography>
          </Stack>
          <DetailCloseButton onClose={onClose} />
        </Stack>
        <Typography variant="subtitleW1">{receipt.vendor_name}</Typography>
      </Stack>

      <ScrollArea className="min-h-0 w-full flex-1">
        <Stack gap="lg" fullWidth className="px-6 py-6">
          <ReceiptFieldsSection receipt={receipt} businessId={businessId} />
          <Separator />
          {receipt.receipt_items.length > 0 && (
            <>
              <LineItemsSection
                items={receipt.receipt_items}
                businessId={businessId}
                documentId={receipt.id}
                documentType="receipt"
                agentRunId={receipt.agent_run_id}
              />
              <Separator />
            </>
          )}
          <DocumentPreviewSection document={receipt} />
          <Separator />
          <AgentActionsSection
            key={receipt.id}
            document={receipt}
            run={getLatestCompletedRun(runs)}
            activeRun={getActiveRun(runs)}
            loading={runsLoading}
            error={runsError}
            onViewAgentReview={(id) => {
              onClose();
              onViewAgentReview?.(id);
            }}
          />
          <Separator />
          <InvoiceActivitySection invoice={receipt} />
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
